import { useEffect, useState } from "react";
import { api } from "../api";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { haptic } from "../telegram";
import { useGame } from "../useGame";

interface Group {
  cat: number;
  title: string;
  cards: string[];
}

interface View {
  /** 16 contents in board order; empty while NYT hasn't answered. */
  cards: string[];
  /** Solved groups, in solve order. */
  solved: Group[];
  mistakes: number;
  maxMistakes: number;
  history: number[][];
  /** After finish: all groups in difficulty order + guess colours. */
  answer?: Group[];
  rows?: number[][];
  nytId?: number;
}

interface GuessExtra {
  result: "solved" | "one_away" | "wrong";
  cat?: number;
  title?: string;
}

/** NYT group colours (yellow → purple), same in both themes — dark text on them. */
const COLORS = ["#f9df6d", "#a0c35a", "#b0c4ef", "#ba81c5"];
const EMOJI = ["🟨", "🟩", "🟦", "🟪"];

const COPY = {
  no_puzzle: "NYT ще не віддав пазл. Спробуй за хвилину.",
  bad_pick: "Вибери рівно 4 картки",
  already_guessed: "Цю четвірку вже пробував",
};

function GroupBar({ group }: { group: Group }) {
  return (
    <div className="rounded-xl px-3 py-2 text-center text-[#1a1a1a]" style={{ background: COLORS[group.cat] }}>
      <div className="text-[12px] font-extrabold uppercase tracking-wide">{group.title}</div>
      <div className="text-[12px] font-medium">{group.cards.join(", ")}</div>
    </div>
  );
}

export function Connections({ date }: { date?: string | undefined }) {
  const g = useGame<View>("connections", COPY, date);
  const [picked, setPicked] = useState<number[]>([]);
  const [order, setOrder] = useState<number[]>(() => Array.from({ length: 16 }, (_, i) => i));

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";

  // Drop picks that belong to a solved group (state refresh, archive reload).
  const solvedContent = new Set((state?.view.solved ?? []).flatMap((s) => s.cards));
  useEffect(() => {
    if (state && picked.some((p) => solvedContent.has(state.view.cards[p] ?? ""))) setPicked([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.view.solved.length]);

  const toggle = (pos: number) => {
    if (finished || g.busy) return;
    haptic.tap();
    setPicked((prev) => (prev.includes(pos) ? prev.filter((p) => p !== pos) : prev.length < 4 ? [...prev, pos] : prev));
  };

  const shuffle = () => {
    setOrder((prev) => {
      const next = [...prev];
      for (let i = next.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [next[i], next[j]] = [next[j] as number, next[i] as number];
      }
      return next;
    });
  };

  const submit = async () => {
    if (picked.length !== 4 || finished || g.busy) return;
    const next = await g.run(() => api.guess<View>("connections", picked.join(","), date));
    if (next) {
      const extra = next.extra as GuessExtra | undefined;
      if (extra?.result === "solved") setPicked([]);
      else if (extra?.result === "one_away") g.showToast("Одна не звідси!");
      else if (extra?.result === "wrong") g.showToast("Ні, не воно");
    }
  };

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const won = s.status === "won";
  const remaining = order.filter((pos) => v.cards[pos] && !solvedContent.has(v.cards[pos] as string));
  const missWord = v.mistakes === 1 ? "помилка" : v.mistakes < 5 ? "помилки" : "помилок";

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="CONNECTIONS" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-2">
        <div className="mt-1 text-center text-xs text-tghint">знайди 4 групи по 4 слова</div>

        <div className="mx-auto mt-3 max-w-[420px] space-y-1.5">
          {(finished ? (v.answer ?? v.solved) : v.solved).map((group) => (
            <GroupBar key={group.cat} group={group} />
          ))}
        </div>

        {!finished && v.cards.length > 0 && (
          <>
            <div className="mx-auto mt-1.5 grid max-w-[420px] grid-cols-4 gap-1.5">
              {remaining.map((pos) => {
                const content = v.cards[pos] as string;
                const active = picked.includes(pos);
                return (
                  <button
                    key={pos}
                    type="button"
                    onClick={() => toggle(pos)}
                    className={`flex h-[68px] items-center justify-center break-words rounded-lg px-1 text-center font-bold uppercase leading-tight transition-colors ${
                      content.length > 8 ? "text-[10px]" : "text-[12px]"
                    } ${active ? "bg-tgbutton text-tgbuttontext" : "bg-tgsection text-tgtext"}`}
                  >
                    {content}
                  </button>
                );
              })}
            </div>

            <div className="mt-3 text-center text-xs text-tghint">
              право на помилку:{" "}
              <span className="tracking-[0.2em]">{"●".repeat(Math.max(0, v.maxMistakes - v.mistakes))}</span>
              {v.mistakes > 0 ? ` · ${v.mistakes} ${missWord}` : ""}
            </div>

            <div className="mx-auto mt-3 flex max-w-[420px] gap-2">
              <button
                type="button"
                onClick={shuffle}
                className="flex-1 rounded-xl bg-[rgba(128,128,128,0.18)] py-3 text-sm font-semibold active:opacity-70"
              >
                🔀 перемішати
              </button>
              <button
                type="button"
                onClick={() => setPicked([])}
                disabled={picked.length === 0}
                className="flex-1 rounded-xl bg-[rgba(128,128,128,0.18)] py-3 text-sm font-semibold active:opacity-70 disabled:opacity-40"
              >
                скинути
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={picked.length !== 4 || g.busy}
                className="flex-1 rounded-xl bg-tgbutton py-3 text-sm font-bold text-tgbuttontext active:brightness-90 disabled:opacity-35"
              >
                {g.busy ? "…" : "готово"}
              </button>
            </div>
          </>
        )}

        {v.cards.length === 0 && <div className="mt-6 text-center text-sm text-tghint">{COPY.no_puzzle}</div>}

        {finished && v.rows && (
          <div className="mt-3 text-center text-lg leading-6">
            {v.rows.map((row, i) => (
              <div key={i}>{row.map((c) => EMOJI[c]).join("")}</div>
            ))}
          </div>
        )}
      </div>

      {!finished && (
        <div className="flex px-4" style={{ paddingBottom: "max(var(--tg-safe-area-inset-bottom, 0px), 10px)" }}>
          <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("connections", date))} />
        </div>
      )}

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? "Є зв'язок!" : "Не склалось"}
          subtitle={
            won
              ? `${v.mistakes === 0 ? "ідеально, без помилок" : `${v.mistakes} ${missWord}`} · стрік ${s.me.streak} 🔥`
              : `знайшов ${v.solved.length}/4 груп · стрік згорів`
          }
          scoreLabel="avg помилок"
          formatScore={(n) => `${n ?? "–"} пом.`}
          onDismiss={() => g.setSheetOpen(false)}
        >
          {v.nytId ? <div className="mt-2 text-center text-xs text-tghint">NYT #{v.nytId.toLocaleString("en-US")}</div> : null}
        </ResultSheet>
      )}
    </Shell>
  );
}
